import api from '@/services/api';
import type { CancelAgreementValues } from '../schemas/cancelAgreement.schema';
import { readString } from '../utils/snapshotReaders';
import type {
    CancelAction,
    CancelAgreementPayload,
    CancellationAwaitingReason,
    CancellationEstimate,
    CancellationProgress,
    CancellationRecord,
    CancellationStarted,
    CancellationUnavailableCode,
    CompletionOption,
    RefundOwedStatus,
} from '../types/cancellation.types';

const asString = (value: unknown): string | null => readString(value) ?? null;
const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' ? (value as Record<string, unknown>) : {});

const mapEstimate = (raw: Record<string, unknown>): CancellationEstimate => ({
    asAt: String(raw.as_at ?? ''),
    fundBalance: String(raw.fund_balance ?? '0'),
    cancellationFee: String(raw.cancellation_fee ?? '0'),
    feeCharged: String(raw.fee_charged ?? '0'),
    feeWaived: raw.fee_waived === true,
    feeWaivedReason: raw.fee_waived_reason === 'cooling_off' ? 'cooling_off' : null,
    coolingOffEndsOn: asString(raw.cooling_off_ends_on),
    refund: String(raw.refund ?? '0'),
    noCustomerRefund: raw.no_customer_refund === true,
});

export const mapCancelAction = (actions: unknown): CancelAction | undefined => {
    const raw = asRecord(actions).cancel;
    if (raw === null || typeof raw !== 'object') return undefined;
    const node = asRecord(raw);
    return {
        available: node.available === true,
        reasonCode: asString(node.reason_code) as CancellationUnavailableCode | null,
        message: asString(node.message),
        estimate: node.estimate ? mapEstimate(asRecord(node.estimate)) : null,
        estimateUnavailable: node.estimate_unavailable === true,
        requiresServiceHistory: node.requires_service_history === true,
    };
};

export const deriveProgress = (node: Record<string, unknown>): CancellationProgress => {
    const awaiting = asString(node.awaiting_motorplan_reason);
    if (awaiting) return { kind: 'awaiting_motorplan', reason: awaiting as CancellationAwaitingReason };
    if (node.halted === true) return { kind: 'awaiting_motorplan', reason: 'integration_fault' };
    if (node.waiting_reason === 'payment_in_flight') return { kind: 'awaiting_clearance' };
    return { kind: 'settling', refundPending: node.waiting_reason === 'refund_pending' };
};

export const mapCancellationRecord = (raw: unknown): CancellationRecord | null | undefined => {
    if (raw === undefined) return undefined;
    if (raw === null || typeof raw !== 'object') return null;
    const node = asRecord(raw);
    return {
        id: String(node.id ?? ''),
        reason: node.reason as CancellationRecord['reason'],
        note: asString(node.note),
        requestingParty: node.requesting_party as CancellationRecord['requestingParty'],
        caseReference: asString(node.case_reference),
        cancelledBy: String(node.cancelled_by ?? ''),
        requestedAt: String(node.requested_at ?? ''),
        cancelledAt: asString(node.cancelled_at),
        figuresFinal: node.fee_charged !== null && node.fee_charged !== undefined,
        feeCharged: String(node.fee_charged ?? '0'),
        feeWaived: node.fee_waived === true,
        feeWaivedReason: node.fee_waived_reason === 'cooling_off' ? 'cooling_off' : null,
        noCustomerRefund: node.no_customer_refund === true,
        refundOwed: asString(node.refund_owed),
        refundStatus: asString(node.refund_status) as RefundOwedStatus | null,
        closedInArtemis: node.closed_in_artemis === true,
        completionNote: asString(node.completion_note),
        completedBy: asString(node.completed_by),
        progress: deriveProgress(node),
    };
};

export const toCancelPayload = (values: CancelAgreementValues): CancelAgreementPayload => ({
    reason: values.reason,
    note: values.note === '' ? null : values.note,
    requesting_party: values.requestingParty,
    case_reference: values.caseReference === '' ? null : values.caseReference,
    ...(values.serviceHistory && {
        service_history_confirmed: true,
        service_history_source: values.serviceHistory.source,
        service_history_detail: values.serviceHistory.detail === '' ? null : values.serviceHistory.detail,
    }),
});

export const startCancellation = async (agreementId: string, values: CancelAgreementValues): Promise<CancellationStarted> => {
    const response = await api.post(`/agreements/${agreementId}/cancellation`, toCancelPayload(values));
    const data = asRecord(response.data?.data);
    return { id: String(data.id ?? ''), status: String(data.status ?? '') };
};

export const completeCancellation = async (agreementId: string, option: CompletionOption, note: string): Promise<void> => {
    await api.post(`/agreements/${agreementId}/cancellation/complete`, { option, note });
};
